import {
  DossDataFormat,
  LookupCellData,
  getEmptyCellData,
  getNumberIndexValue,
  isArrayCellData,
  isBooleanCellData,
  isDateCellData,
  isDateTimeCellData,
  isEnumCellData,
  isIdCellData,
  isLookupCellData,
  isNumberCellData,
  isStringCellData,
} from '@doss/common-all'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { compact, difference, includes, isEmpty, isEqual, isNil } from 'lodash'
import { DataType, IndexType, TableColumn } from '~/common/generated/apollo-operations'
import { JSONObject } from '~/common/scalars'
import {
  CellValue,
  LookupCellValue,
  isLookupCellValue,
} from '~/components/records/add-record/AddRecordDialog'
import { RecordDetailTableCell } from '~/components/records/types'
import { ActiveUser } from '../../components/settings/types'
import { TranslationKeys } from '../generated/i18n.generated'

dayjs.extend(utc)

/**
 * Checks if the cell value passed in is a valid value for updating a lookup cell
 */
function isValidLookupValue(value: unknown): value is LookupCellValue[] | LookupCellData {
  if (Array.isArray(value) && value.every(isLookupCellValue)) {
    return true
  }
  return typeof value === 'object' && isLookupCellData(value)
}

/**
 * Determines whether a lookup cell value has changed by checking if the lookup cell ids have
 * changed or, if overwriting the lookup, checks if the data has changed
 */
export function hasLookupCellDataChanged(
  originalValue: JSONObject | null,
  value: LookupCellValue[] | LookupCellData | null | undefined,
  originalLookupCellIds: string[],
): boolean {
  // If original value had no selected cells and no overwritten value, return true if updated value
  // has cells or an overwritten value
  const originalValueIsEmpty =
    isEmpty(originalLookupCellIds) &&
    (isNil(originalValue) || (isLookupCellData(originalValue) && isNil(originalValue.data)))

  const updatedValueIsEmpty =
    isNil(value) ||
    (Array.isArray(value) && isEmpty(value)) ||
    (!Array.isArray(value) && isLookupCellData(value) && isNil(value.data))

  if (originalValueIsEmpty) {
    // If the new value is null or undefined, return false
    return !updatedValueIsEmpty
  }

  // If updated value has no selected cells and no overwritten value, return true if the original
  // value had selected cells or an overwritten value
  if (updatedValueIsEmpty) {
    return !originalValueIsEmpty
  }

  // If original value had lookup cells, check if the updated value has different selected cells
  // or an overwritten value
  if (!isEmpty(originalLookupCellIds)) {
    // If the updated value has an overwritten value, return true
    if (!Array.isArray(value)) {
      return true
    }
    // If updated value is an array of lookup cells, check if the lengths are different
    if (value.length !== originalLookupCellIds.length) {
      return true
    }
    // If the number of selected cells is the same, check if the ids are different
    const updatedLookupCellIds = value.map((lookupCellValue) => lookupCellValue.id)
    return !originalLookupCellIds.every((lookupCellId) =>
      updatedLookupCellIds.includes(lookupCellId),
    )
  }

  // Fallback for the case where originalValue is a LookupCellData with a cached resolved value
  // but cell.lookupCells is empty (Apollo can drop it from cache when a sibling query returns
  // a TableCell shape without lookupCells — observed for lookups whose target column is a
  // server-managed / readonly column). If the user has now picked rows, that's a change.
  if (isLookupCellData(originalValue) && Array.isArray(value) && !isEmpty(value)) {
    return true
  }

  // Default case
  return false
}

export function hasCellDataChanged(
  column: Pick<TableColumn, 'type' | 'indexType' | 'innerType'>,
  originalValue: JSONObject | null,
  value: CellValue,
  originalLookupCellIds: string[],
  originalDocumentIds: string[],
  originalUserIds: string[],
) {
  if (
    column.type === DataType.FORMULA ||
    column.type === DataType.FORMULA_NUMBER ||
    column.type === DataType.FORMULA_STRING ||
    column.type === DataType.FORMULA_DATE ||
    column.type === DataType.FORMULA_DATE_TIME
  ) {
    // Formula value cannot change
    return false
  }
  if (column.type === DataType.LOOKUP) {
    // A lookup can have null resolved display data while still having selected lookup cells.
    const lookupValue = value === '' ? null : value
    if (!isNil(lookupValue) && !isValidLookupValue(lookupValue)) {
      throw new Error(`Invalid lookup value type: ${String(lookupValue)}`)
    }
    return hasLookupCellDataChanged(originalValue, lookupValue, originalLookupCellIds)
  }
  if (column.type === DataType.FILE_URL) {
    // For files, value will be null or empty string while originalDocumentIds will be empty
    const nullValue = value === null || value === ''
    const emptyFiles = originalDocumentIds.length === 0
    if (nullValue !== emptyFiles) {
      return true
    }
  } else if (column.type === DataType.USER) {
    // For useres, value will be null or empty string while originalUserIds will be empty
    const nullValue = value === null || value === ''
    const emptyUsers = originalUserIds.length === 0
    if (nullValue !== emptyUsers) {
      return true
    }
  } else if (column.type === DataType.ARRAY) {
    // Special handling for array type columns

    // Handle case when original value is just { type: 'ARRAY' } without data property
    // which happens for non-admin users
    const originalIsEmptyArray =
      isNil(originalValue) ||
      (originalValue.type === DossDataFormat.ARRAY &&
        (!('data' in originalValue) ||
          (Array.isArray(originalValue.data) && originalValue.data.length === 0)))

    const valueIsEmptyArray = isNil(value) || (Array.isArray(value) && value.length === 0)

    if (originalIsEmptyArray !== valueIsEmptyArray) {
      // One is empty and the other isn't
      return true
    } else if (originalIsEmptyArray && valueIsEmptyArray) {
      // Both are empty in some form, no change
      return false
    }
  } else if (
    (isNil(value) || value === '') !==
    (isNil(originalValue) || (isStringCellData(originalValue) && originalValue.data === ''))
  ) {
    // If the value has flipped between being set and not being set, it has changed
    return true
  } else if (
    isNil(originalValue) ||
    (isStringCellData(originalValue) && originalValue.data === '')
  ) {
    // If the value isn't set originally or now, it hasn't changed
    return false
  }

  let isChanged = false
  switch (column.type) {
    case DataType.ARRAY: {
      // Moving this outside of the isArrayCellData check since lookups might have a null value
      // because the lookup cell, though it exists, has no value
      if (column.innerType === DataType.LOOKUP) {
        if (!value || !Array.isArray(value) || value.some((cell) => typeof cell === 'string')) {
          isChanged = true
        } else if (Array.isArray(value) && (isLookupCellValue(value[0]) || value.length === 0)) {
          const lookupValues = value as LookupCellValue[]
          const newLookupIds = lookupValues.map((cell) => cell.id)
          const hasNewCells = difference(newLookupIds, originalLookupCellIds).length > 0
          const hasRemovedCells = difference(originalLookupCellIds, newLookupIds).length > 0
          isChanged = hasNewCells || hasRemovedCells
        }
      }
      if (isArrayCellData(originalValue)) {
        if (column.innerType === DataType.FILE_URL) {
          if (!value || !Array.isArray(value)) {
            isChanged = true
          } else {
            // At this point, we know that value is a string[] but we have to cast
            const newDocumentIds = value as string[]
            const hasNewDocuments = difference(newDocumentIds, originalDocumentIds).length > 0
            const hasRemovedDocuments = difference(originalDocumentIds, newDocumentIds).length > 0
            isChanged = hasNewDocuments || hasRemovedDocuments
          }
        } else if (column.innerType === DataType.USER) {
          if (!value || !Array.isArray(value)) {
            isChanged = true
          } else {
            // At this point, we know that value is a string[] but we have to cast
            const newUserIds = (value as ActiveUser[]).map((user) => user.id)
            const hasNewUsers = difference(newUserIds, originalUserIds).length > 0
            const hasRemovedUsers = difference(originalUserIds, newUserIds).length > 0
            isChanged = hasNewUsers || hasRemovedUsers
          }
        } else {
          isChanged = !isEqual(originalValue.data, value)
        }
      }
      break
    }
    case DataType.BOOLEAN: {
      let typedValue = false
      if (typeof value === 'boolean') {
        typedValue = value
      } else if (typeof value === 'string') {
        typedValue = value === 'true'
      }

      if (isBooleanCellData(originalValue)) {
        isChanged = originalValue.data !== typedValue
      }
      break
    }
    case DataType.DATE: {
      const typeOfValue = typeof value
      const isValidDateValue = typeOfValue === 'string' || dayjs.isDayjs(value)
      if (isDateCellData(originalValue) && isValidDateValue) {
        // Normalize both to UTC midnight before comparing
        const orig = dayjs.utc(originalValue.data).startOf('day')
        const val = dayjs.utc(value as string | dayjs.Dayjs).startOf('day')
        isChanged = !orig.isSame(val)
      }
      break
    }
    case DataType.DATE_TIME: {
      const typeOfValue = typeof value
      const isValidDateValue = typeOfValue === 'string' || dayjs.isDayjs(value)
      if (isDateTimeCellData(originalValue) && isValidDateValue) {
        // Normalize both to UTC midnight before comparing
        const orig = dayjs.utc(originalValue.data).startOf('minute')
        const val = dayjs.utc(value as string | dayjs.Dayjs).startOf('minute')
        isChanged = !orig.isSame(val)
      }

      break
    }
    case DataType.ENUM: {
      if (isEnumCellData(originalValue)) {
        isChanged = originalValue.data !== value
      }

      break
    }
    case DataType.FILE_URL: {
      if (value && typeof value === 'string') {
        if (originalDocumentIds.length !== 1) {
          isChanged = true
        } else {
          isChanged = originalDocumentIds[0] !== value
        }
      }
      break
    }
    case DataType.USER: {
      // If the value is an empty string or null, check if the original value had any users
      if ((Array.isArray(value) && value.length === 0) || !value) {
        isChanged = originalUserIds.length > 0
      } else if (Array.isArray(value) && value.every((user) => typeof user === 'object')) {
        // If the value is an array of users, check if the user ids are different
        const newUserIds = (value as ActiveUser[]).map((user) => user.id)
        isChanged = !isEqual(originalUserIds, newUserIds)
      }
      break
    }
    case DataType.ID: {
      if (isIdCellData(originalValue)) {
        const typedValue =
          column.indexType === IndexType.NUMBER ? getNumberIndexValue(value) : value
        isChanged = originalValue.data !== typedValue
      }

      break
    }
    case DataType.NUMBER: {
      if (isNumberCellData(originalValue)) {
        const typedValue = isNil(value) ? null : parseFloat(value as string)
        isChanged = originalValue.data !== typedValue
      }

      break
    }
    case DataType.STRING: {
      if (isStringCellData(originalValue)) {
        isChanged = originalValue.data !== value
      }

      break
    }
    case DataType.FORMULA_BOOLEAN:
      break
  }

  return isChanged
}

type CellData = RecordDetailTableCell | JSONObject | null

type CellDataForUpdate = {
  cellData: CellData
  lookupCellIds: string[] | null
  documentIds: string[] | null
  userIds: string[] | null
  deletedFileIds: string[] | null
  tableColumnId: string
}

/**
 * When updating a table cell, we need to manipulate the user entered data in the following ways:
 * - if the user value is null, set the cell data to null and if a file was deleted, return the
 *   file's id so it can be deleted from the cache
 * - if the user value is not null but the cell data is null, check if the cell is a file cell. if
 *   it is, set the cell data as a file cell
 * - if both values are not null:
 *   - if the cell data is a lookup cell, set the lookup cell id rather than the data
 *   - if the cell data is a boolean cell, set the value to a boolean
 *   - if the cell data is a number cell, set the value to a number
 */
export function getCellDataForUpdate(
  cell: RecordDetailTableCell,
  value: CellValue,
  showError: (message: string) => void,
  t: (key: TranslationKeys) => string,
): CellDataForUpdate | undefined {
  let cellData = cell.data ?? getEmptyCellData(cell.column)
  let lookupCellIds: string[] | null = null
  let documentIds: string[] | null = null
  let userIds: string[] | null = null

  // If the cell is a file cell that was cleared, set the deletedFileId
  let deletedFileIds: string[] | null = null

  let strippedValue = value
  if (typeof value === 'string') {
    strippedValue = value.trim()
  }

  // If the value is null, set the cell data to null
  if (isNil(strippedValue) || strippedValue === '') {
    cellData = null
    if (cell.documents.length > 0) {
      deletedFileIds = cell.documents.map((document) => document.id)
      cellData = { type: DossDataFormat.FILE }
    } else if (cell.column.type === DataType.LOOKUP) {
      cellData = { type: DossDataFormat.LOOKUP }
    } else if (cell.column.type === DataType.USER) {
      cellData = { type: DossDataFormat.USER }
    }
    return {
      cellData,
      lookupCellIds,
      deletedFileIds,
      documentIds,
      userIds,
      tableColumnId: cell.column.id,
    }
  }

  switch (cell.column.type) {
    case DataType.ARRAY: {
      if (!Array.isArray(strippedValue)) {
        showError(t('table.cell.not_array'))
        return
      }
      if (cell.column.innerType === DataType.LOOKUP) {
        const cellIds = strippedValue.map((cell) => {
          if (typeof cell !== 'object' || Array.isArray(cell)) {
            showError(t('table.cell.not_lookup'))
            return null
          }
          return cell.id
        })
        cellData = { type: DossDataFormat.ARRAY, data: [] }
        lookupCellIds = compact(cellIds)
        // Check that is a string array
      } else if (cell.column.innerType === DataType.FILE_URL) {
        const fileIds = strippedValue.map((file) => {
          if (typeof file !== 'string') {
            showError(t('table.cell.not_file'))
            return null
          }
          return file
        })
        cellData = { type: DossDataFormat.ARRAY, data: [] }
        documentIds = compact(fileIds)
      } else if (cell.column.innerType === DataType.USER) {
        const strippedUserIds = strippedValue.map((user) => {
          if (typeof user !== 'object' || Array.isArray(user)) {
            showError(t('table.cell.not_user'))
            return null
          }
          return user.id
        })
        cellData = { type: DossDataFormat.ARRAY, data: [] }
        userIds = compact(strippedUserIds)
      } else if (strippedValue.some((value) => typeof value === 'string')) {
        // Typescript doesn't recognize that the if condition enfoces that the value is a string[]
        // so we still have to cast it
        cellData = {
          type: DossDataFormat.ARRAY,
          data: strippedValue as string[],
        }
      } else if (strippedValue.length === 0) {
        cellData = { type: DossDataFormat.ARRAY, data: [] }
      } else {
        showError(t('table.cell.not_string_array'))
        return
      }
      break
    }
    case DataType.BOOLEAN: {
      let booleanValue: boolean
      if (typeof strippedValue === 'string') {
        booleanValue = strippedValue === 'true'
      } else {
        booleanValue = Boolean(strippedValue)
      }
      cellData = { type: DossDataFormat.BOOLEAN, data: booleanValue }
      break
    }
    case DataType.DATE: {
      if (typeof strippedValue !== 'string') {
        showError(t('table.cell.not_string'))
        return
      }
      cellData = { type: DossDataFormat.DATE, data: strippedValue }
      break
    }
    case DataType.DATE_TIME: {
      if (typeof strippedValue !== 'string') {
        showError(t('table.cell.not_string'))
        return
      }
      cellData = { type: DossDataFormat.DATE_TIME, data: strippedValue }
      break
    }
    case DataType.ENUM: {
      if (typeof strippedValue !== 'string') {
        showError(t('table.cell.not_string'))
        return
      }
      cellData = { type: DossDataFormat.ENUM, data: strippedValue }
      break
    }
    case DataType.FILE_URL: {
      if (typeof strippedValue !== 'string') {
        showError(t('table.cell.not_string'))
        return
      }
      cellData = { type: DossDataFormat.FILE }
      documentIds = [strippedValue]
      break
    }
    case DataType.USER: {
      if (!Array.isArray(strippedValue)) {
        showError(t('table.cell.not_user'))
        return
      }
      const firstValue = strippedValue[0]
      if (typeof firstValue === 'object' && 'firstName' in firstValue) {
        cellData = { type: DossDataFormat.USER }
        userIds = [firstValue.id]
      }
      break
    }
    case DataType.FORMULA:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME: {
      showError(t('table.cell.cannot_update_formula'))
      return
    }
    // FORMULA_BOOLEAN is intentionally out of scope for the non-numeric-formulas epic (see
    // isFormula()'s doc comment in @doss/common-all's table-cell.ts) -- no cell-data schema or
    // resolver exists for it yet.
    case DataType.FORMULA_BOOLEAN:
      return
    case DataType.ID: {
      if (cell.column.indexType === IndexType.NUMBER) {
        strippedValue = Number(strippedValue)
        cellData = { type: DossDataFormat.ID, data: strippedValue }
      } else {
        cellData = { type: DossDataFormat.ID, data: strippedValue.toString() }
      }
      break
    }
    case DataType.LOOKUP: {
      if (Array.isArray(strippedValue)) {
        if (isEmpty(strippedValue)) {
          cellData = { type: DossDataFormat.LOOKUP }
          lookupCellIds = []
        } else {
          const firstValue = strippedValue[0]
          if (typeof firstValue === 'object' && 'id' in firstValue) {
            cellData = { type: DossDataFormat.LOOKUP }
            lookupCellIds = [firstValue.id]
          }
        }
      } else if (typeof strippedValue === 'object' && isLookupCellData(strippedValue)) {
        cellData = strippedValue
        lookupCellIds = []
      }

      break
    }
    case DataType.NUMBER: {
      if (typeof strippedValue === 'string') {
        cellData = {
          type: DossDataFormat.NUMBER,
          data: parseFloat(strippedValue),
        }
      } else if (typeof strippedValue === 'number') {
        cellData = { type: DossDataFormat.NUMBER, data: strippedValue }
      }
      break
    }
    case DataType.STRING: {
      if (typeof strippedValue !== 'string') {
        showError(t('table.cell.not_string'))
        return
      }
      cellData = { type: DossDataFormat.STRING, data: strippedValue }
      break
    }
  }

  return {
    cellData,
    lookupCellIds,
    deletedFileIds,
    documentIds,
    userIds,
    tableColumnId: cell.column.id,
  }
}

/**
 * Checks if the documentId provided is already in the cell data
 */
export function isDocumentIdInCell(documents: { id: string }[], documentId: string): boolean {
  const values: string[] = documents.map((document) => document.id)
  return includes(values, documentId)
}
